import React, { useState } from 'react';
import { Upload, FileText, Loader2, X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { primaryText, semiBoldText, baseTextSize, smallTextSize } from '../../../../styling/stylingHelper';
import { processRecipeAutomation } from '../../../../services/recipes/recipeAutomationService';
import { useTranslation } from 'react-i18next';
import { RevenueSourceType, getRevenueSourceTypeDisplayName } from '../../../../types/RevenueSourceType';
import { SupportedLanguage } from '../../../../services/recipes/recipesService';
import { useMenusStore } from '../../../../stores/appMenusStore';
import AllergenImportReview from '../../../recipes/AllergenImportReview';

interface RecipeAutomationModalProps {
  restaurantId: string;
  // When the recipes page menu filter has exactly one real menu selected, we pass it
  // here so the picker defaults to it (with a note). Optional.
  defaultMenuId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export type RecipeAutomationType = 'RECIPE_COMPONENTS' | 'FULL_RECIPES' | 'ALLERGIES_IMPORT';

const NO_MENU = '';

const RecipeAutomationModal: React.FC<RecipeAutomationModalProps> = ({
  restaurantId,
  defaultMenuId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [automationType, setAutomationType] = useState<RecipeAutomationType>('RECIPE_COMPONENTS');
  const [revenueSourceType, setRevenueSourceType] = useState<RevenueSourceType | 'ALL'>('ALL');
  const [sourceLanguage, setSourceLanguage] = useState<SupportedLanguage>('en');
  const [selectedMenuId, setSelectedMenuId] = useState<string>(defaultMenuId ?? NO_MENU);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const menus = useMenusStore((s) => s.menus);
  const loadMenuItems = useMenusStore((s) => s.loadMenuItems);

  // Menu assignment only applies to RECIPE_COMPONENTS (the only type that links recipes to sales items).
  const menuAssignmentEnabled = automationType === 'RECIPE_COMPONENTS';
  const showDefaultedMenuNote = !!defaultMenuId && selectedMenuId === defaultMenuId;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setResult(null);
      setError(null);
    }
  };

  const handleAutomationTypeChange = (type: RecipeAutomationType) => {
    setAutomationType(type);
    setSelectedFile(null); // Clear file when switching types
    setResult(null);
    setError(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      const expectedExtension = '.docx';
      const allowedExtensions = ['.docx', '.doc'];

      if (!allowedExtensions.includes(fileExtension)) {
        setError(`Invalid file type. Please upload a ${expectedExtension} file.`);
        return;
      }

      setSelectedFile(file);
      setResult(null);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setResult(null);

    try {
      const menuIdToSend = menuAssignmentEnabled && selectedMenuId ? selectedMenuId : undefined;
      const data = await processRecipeAutomation(restaurantId, selectedFile, automationType, revenueSourceType, sourceLanguage, menuIdToSend);
      setResult(data);
      // Refresh the assigned menu's items so the cost price / recipes pages reflect the change.
      if (menuIdToSend && (data.itemsAssignedToMenu ?? 0) > 0) {
        try {
          await loadMenuItems(menuIdToSend);
        } catch (err) {
          console.error('Failed to refresh menu items after recipe automation:', err);
        }
      }
      // For non-allergies automation, auto-close after showing success
      // For allergies import, keep modal open so user can review results
      if (automationType !== 'ALLERGIES_IMPORT' && onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while processing the recipe file');
    } finally {
      setIsProcessing(false);
    }
  };

  if (automationType === 'ALLERGIES_IMPORT') {
    return <AllergenImportReview key={restaurantId} restaurantId={restaurantId} onClose={onClose}
      onSuccess={onSuccess} onBack={() => handleAutomationTypeChange('RECIPE_COMPONENTS')} />;
  }

  return (
    <div className="fixed inset-0 bg-black dark:bg-black bg-opacity-50 dark:bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background dark:bg-background-dark rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className={cn(primaryText, semiBoldText, baseTextSize)}>
            {t('recipes.automationModal.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
              <p className={cn(primaryText, smallTextSize, "opacity-70")}>
                {t('recipes.automationModal.description')}
              </p>

              {/* Automation Type Selection */}
              <div>
                <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
                  {t('recipes.automationModal.automationType')}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <button
                    onClick={() => handleAutomationTypeChange('RECIPE_COMPONENTS')}
                    className={cn(
                      "p-4 border-2 rounded-md transition-all",
                      automationType === 'RECIPE_COMPONENTS'
                        ? "border-accent dark:border-accent-dark bg-accent/5 dark:bg-accent-dark/5"
                        : "border-gray-300 dark:border-gray-600 hover:border-accent/50 dark:hover:border-accent-dark/50"
                    )}
                  >
                    <div className={cn(primaryText, semiBoldText, smallTextSize, "mb-1")}>
                      {t('recipes.automationModal.recipeComponents')}
                    </div>
                    <div className={cn(primaryText, "text-xs opacity-60")}>
                      {t('recipes.automationModal.recipeComponentsDescription')}
                    </div>
                  </button>
                  <button
                    onClick={() => handleAutomationTypeChange('FULL_RECIPES')}
                    className={cn(
                      "p-4 border-2 rounded-md transition-all",
                      automationType === 'FULL_RECIPES'
                        ? "border-accent dark:border-accent-dark bg-accent/5 dark:bg-accent-dark/5"
                        : "border-gray-300 dark:border-gray-600 hover:border-accent/50 dark:hover:border-accent-dark/50"
                    )}
                  >
                    <div className={cn(primaryText, semiBoldText, smallTextSize, "mb-1")}>
                      {t('recipes.automationModal.fullRecipes')}
                    </div>
                    <div className={cn(primaryText, "text-xs opacity-60")}>
                      {t('recipes.automationModal.fullRecipesDescription')}
                    </div>
                  </button>
                  <button
                    onClick={() => handleAutomationTypeChange('ALLERGIES_IMPORT')}
                    className={cn(
                      "p-4 border-2 rounded-md transition-all",
                      "border-gray-300 dark:border-gray-600 hover:border-accent/50 dark:hover:border-accent-dark/50"
                    )}
                  >
                    <div className={cn(primaryText, semiBoldText, smallTextSize, "mb-1")}>
                      {t('recipes.automationModal.allergiesImport')}
                    </div>
                    <div className={cn(primaryText, "text-xs opacity-60")}>
                      {t('recipes.automationModal.allergiesImportDescription')}
                    </div>
                  </button>
                </div>
              </div>

              {/* Revenue Source Type Selection - Only shown for non-allergies automation */}
              {(
                <div>
                  <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
                    {t('recipes.automationModal.revenueSourceType')}
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <button
                      onClick={() => setRevenueSourceType('ALL')}
                      className={cn(
                        "p-3 border-2 rounded-md transition-all",
                        revenueSourceType === 'ALL'
                          ? "border-accent dark:border-accent-dark bg-accent/5 dark:bg-accent-dark/5"
                          : "border-gray-300 dark:border-gray-600 hover:border-accent/50 dark:hover:border-accent-dark/50"
                      )}
                    >
                      <div className={cn(primaryText, semiBoldText, smallTextSize)}>
                        {t('recipes.automationModal.all')}
                      </div>
                    </button>
                    <button
                      onClick={() => setRevenueSourceType(RevenueSourceType.FOOD)}
                      className={cn(
                        "p-3 border-2 rounded-md transition-all",
                        revenueSourceType === RevenueSourceType.FOOD
                          ? "border-accent dark:border-accent-dark bg-accent/5 dark:bg-accent-dark/5"
                          : "border-gray-300 dark:border-gray-600 hover:border-accent/50 dark:hover:border-accent-dark/50"
                      )}
                    >
                      <div className={cn(primaryText, semiBoldText, smallTextSize)}>
                        {getRevenueSourceTypeDisplayName(RevenueSourceType.FOOD)}
                      </div>
                    </button>
                    <button
                      onClick={() => setRevenueSourceType(RevenueSourceType.DRINKS)}
                      className={cn(
                        "p-3 border-2 rounded-md transition-all",
                        revenueSourceType === RevenueSourceType.DRINKS
                          ? "border-accent dark:border-accent-dark bg-accent/5 dark:bg-accent-dark/5"
                          : "border-gray-300 dark:border-gray-600 hover:border-accent/50 dark:hover:border-accent-dark/50"
                      )}
                    >
                      <div className={cn(primaryText, semiBoldText, smallTextSize)}>
                        {getRevenueSourceTypeDisplayName(RevenueSourceType.DRINKS)}
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Source Language Selection - Only shown for non-allergies automation */}
              {(
                <div>
                  <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
                    {t('recipes.automationModal.sourceLanguage')}
                  </label>
                  <p className={cn(primaryText, "text-xs opacity-60 mb-2")}>
                    {t('recipes.automationModal.sourceLanguageDescription')}
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      onClick={() => setSourceLanguage('en')}
                      className={cn(
                        "p-3 border-2 rounded-md transition-all",
                        sourceLanguage === 'en'
                          ? "border-accent dark:border-accent-dark bg-accent/5 dark:bg-accent-dark/5"
                          : "border-gray-300 dark:border-gray-600 hover:border-accent/50 dark:hover:border-accent-dark/50"
                      )}
                    >
                      <div className={cn(primaryText, semiBoldText, smallTextSize)}>
                        English
                      </div>
                    </button>
                    <button
                      onClick={() => setSourceLanguage('es')}
                      className={cn(
                        "p-3 border-2 rounded-md transition-all",
                        sourceLanguage === 'es'
                          ? "border-accent dark:border-accent-dark bg-accent/5 dark:bg-accent-dark/5"
                          : "border-gray-300 dark:border-gray-600 hover:border-accent/50 dark:hover:border-accent-dark/50"
                      )}
                    >
                      <div className={cn(primaryText, semiBoldText, smallTextSize)}>
                        Español
                      </div>
                    </button>
                    <button
                      onClick={() => setSourceLanguage('nl')}
                      className={cn(
                        "p-3 border-2 rounded-md transition-all",
                        sourceLanguage === 'nl'
                          ? "border-accent dark:border-accent-dark bg-accent/5 dark:bg-accent-dark/5"
                          : "border-gray-300 dark:border-gray-600 hover:border-accent/50 dark:hover:border-accent-dark/50"
                      )}
                    >
                      <div className={cn(primaryText, semiBoldText, smallTextSize)}>
                        Nederlands
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Menu Assignment - Only for RECIPE_COMPONENTS (the type that links recipes to sales items) */}
              {menuAssignmentEnabled && (
                <div>
                  <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
                    {t('recipes.automationModal.assignToMenu')}
                  </label>
                  <p className={cn(primaryText, "text-xs opacity-60 mb-2")}>
                    {t('recipes.automationModal.assignToMenuDescription')}
                  </p>
                  <select
                    value={selectedMenuId}
                    onChange={(e) => setSelectedMenuId(e.target.value)}
                    disabled={isProcessing}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background dark:bg-background-dark text-primaryText dark:text-primaryText-dark"
                  >
                    <option value={NO_MENU}>{t('recipes.automationModal.noMenu')}</option>
                    {menus.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}{m.isActive ? '' : ` ${t('recipes.automationModal.menuInactive')}`}
                      </option>
                    ))}
                  </select>
                  {showDefaultedMenuNote && (
                    <div className="mt-2 flex items-start gap-2 text-xs text-blue-700 dark:text-blue-300">
                      <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>{t('recipes.automationModal.assignToMenuDefaultedNote')}</span>
                    </div>
                  )}
                </div>
              )}

              {/* File Upload */}
              <div>
                <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
                  {t('recipes.automationModal.uploadRecipeFile')}
                </label>
                <div className="flex items-center gap-4">
                  <label
                    className="flex-1 flex items-center justify-center px-4 py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:border-accent dark:hover:border-accent-dark transition-colors"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <div className="text-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-2" />
                      <span className={cn(primaryText, smallTextSize)}>
                        {selectedFile ? selectedFile.name : t('recipes.automationModal.uploadPlaceholder')}
                      </span>
                      <p className={cn(primaryText, "text-xs opacity-60 mt-1")}>
                        {t('recipes.automationModal.fileTypeDescription')}
                      </p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept={'.docx,.doc'}
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
                {selectedFile && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <FileText className="h-4 w-4" />
                    <span>{selectedFile.name}</span>
                  </div>
                )}
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <h3 className={cn(primaryText, semiBoldText, smallTextSize, "text-red-800 dark:text-red-300 mb-1")}>
                      {t('recipes.automationModal.error')}
                    </h3>
                    {/* Validation failures are a bullet list of lines to fix — keep the line breaks */}
                    <p className={cn(primaryText, "text-sm text-red-700 dark:text-red-400 whitespace-pre-line break-words max-h-60 overflow-y-auto")}>
                      {error}
                    </p>
                  </div>
                </div>
              )}

              {/* Result Display for non-allergies automation */}
              {result && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className={cn(primaryText, semiBoldText, smallTextSize, "text-green-800 dark:text-green-300 mb-1")}>
                        {t('recipes.automationModal.processingSuccessful')}
                      </h3>
                      <p className={cn(primaryText, "text-sm text-green-700 dark:text-green-400")}>
                        {result.message || 'Recipe file processed successfully'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-primaryText dark:text-primaryText-dark rounded-md font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  {t('recipes.automationModal.cancel')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!selectedFile || isProcessing}
                  className="flex-1 px-6 py-3 bg-accent dark:bg-accent-dark text-white dark:text-white rounded-md font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {t('recipes.automationModal.processing')}
                    </>
                  ) : (
                    <>
                      <Upload className="h-5 w-5" />
                      {t('recipes.automationModal.processRecipe')}
                    </>
                  )}
                </button>
              </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeAutomationModal;
